import React, { useState, useEffect } from "react";
import { get, post } from "../../services/api";
// import "./PTMParentStyles.css";

function PtmBookingParent() {
  const [ptmEvent, setPtmEvent] = useState(null);
  const [teachers, setTeachers] = useState([]);
  const [myBookings, setMyBookings] = useState([]);
  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentId, setStudentId] = useState(null);
  const [studentName, setStudentName] = useState("");
  const [activeTab, setActiveTab] = useState("booking");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("");

  const token = localStorage.getItem("token");
  const user = localStorage.getItem("userId");
  const centerId = localStorage.getItem("centerId");

  useEffect(() => {
    initializeData();
  }, []);

  const initializeData = async () => {
    try {
      await fetchParentStudents();
    } catch (error) {
      showMessage("Error initializing data: " + error.message, "error");
    }
  };

  // Fetch parent's students - NO AUTO SELECTION
  const fetchParentStudents = async () => {
    try {
      setLoading(true);
      const studentsData = await get(
        `Student/GetStudentByClassSectionIdAsync?classSectionId=0&userId=${user}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (studentsData && studentsData.length > 0) {
        setStudents(studentsData);
        // DON'T auto-select - let user choose
      } else {
        showMessage("No students found linked to your account", "error");
        setStudents([]);
      }
    } catch (error) {
      showMessage("Error fetching students: " + error.message, "error");
      setStudents([]);
    } finally {
      setLoading(false);
    }
  };

  // Select a student and load their PTM data
  const handleSelectStudent = async (student) => {
    try {
      setLoading(true);
      setMessage(""); // Clear any existing messages
      setSelectedStudent(student);
      setStudentId(student.id);
      setStudentName(student.studentName);

      // Fetch PTM event and teachers for this student
      await fetchPTMEvent(student.id);
      await fetchMyBookings();

      setActiveTab("booking");
    } catch (error) {
      showMessage("Error selecting student: " + error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  // Get current event for center and available teachers for student
  const fetchPTMEvent = async (stId) => {
    try {
      const eventData = await get(`PTM/Parent/GetCurrentEvent/${centerId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setPtmEvent(eventData);

      // Get available teachers for this specific student
      const teachersData = await get(
        `PTM/Parent/GetAvailableTeachers/${stId}/${centerId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setTeachers(teachersData || []);

      if (!teachersData || teachersData.length === 0) {
        showMessage(
          "No teachers available for this student at this time",
          "info"
        );
      }
    } catch (error) {
      showMessage("Error fetching PTM data: " + error.message, "error");
    }
  };

  // Get parent's existing bookings
  const fetchMyBookings = async () => {
    try {
      const bookingsData = await get(`PTM/Parent/GetMyBookings/${user}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setMyBookings(bookingsData || []);
    } catch (error) {
      console.error("Error fetching bookings:", error);
      setMyBookings([]);
    }
  };

  const handleBookSlot = async (ptmTeacherId, slotId) => {
    try {
      setLoading(true);

      const bookingPayload = {
        ptmEventId: ptmEvent.id,
        ptmEventTeacherId: ptmTeacherId,
        ptmSlotId: slotId,
        studentId: studentId,
        studentName: studentName,
        userId: parseInt(user),
      };

      await post("PTM/Parent/BookSlot", bookingPayload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      showMessage("Slot booked successfully!", "success");
      await fetchPTMEvent(studentId);
      await fetchMyBookings();

      setTimeout(() => setActiveTab("my-bookings"), 1500);
    } catch (error) {
      const errorMessage = error.response?.data?.message || "Booking failed";
      showMessage(errorMessage, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleCancelBooking = async (bookingId) => {
    if (!window.confirm("Are you sure you want to cancel this booking?"))
      return;

    try {
      setLoading(true);

      await post(
        `PTM/Parent/CancelBooking/${bookingId}/${user}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      showMessage("Booking cancelled successfully!", "success");
      await fetchPTMEvent(studentId);
      await fetchMyBookings();
    } catch (error) {
      showMessage("Error cancelling booking: " + error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (msg, type) => {
    setMessage(msg);
    setMessageType(type);
    // Don't auto-hide error messages
    if (type === "success") {
      setTimeout(() => setMessage(""), 5000);
    }
  };

  const handleChangeStudent = () => {
    setSelectedStudent(null);
    setStudentId(null);
    setStudentName("");
    setPtmEvent(null);
    setTeachers([]);
    setMessage("");
  };

  if (loading && students.length === 0) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "400px" }}
      >
        <div className="text-center">
          <div className="spinner-border text-primary mb-3" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="text-muted">Loading students...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-4">
      {/* Persistent Message Alert */}
      {message && (
        <div
          className={`alert alert-${
            messageType === "success"
              ? "success"
              : messageType === "error"
              ? "danger"
              : "info"
          } alert-dismissible fade show sticky-top`}
          role="alert"
          style={{ zIndex: 1050 }}
        >
          <div className="d-flex align-items-center">
            <span className="me-2">
              {messageType === "success"
                ? "✅"
                : messageType === "error"
                ? "❌"
                : "ℹ️"}
            </span>
            <span>{message}</span>
          </div>
          <button
            type="button"
            className="btn-close"
            onClick={() => setMessage("")}
            aria-label="Close"
          ></button>
        </div>
      )}

      {/* Student Selection Screen */}
      {!selectedStudent && (
        <div className="card shadow-sm">
          <div className="card-header bg-primary text-white">
            <h4 className="mb-0 text-white">
              <i className="bi bi-person-circle me-2 text-white"></i>
              Select Student for PTM Booking
            </h4>
          </div>
          <div className="card-body">
            {students.length === 0 ? (
              <div className="alert alert-warning mb-0">
                <i className="bi bi-exclamation-triangle me-2"></i>
                No students are linked to your account. Please contact the
                administration.
              </div>
            ) : (
              <div className="row g-4">
                {students.map((student) => (
                  <div key={student.id} className="col-md-6 col-lg-4">
                    <div
                      className="card h-100 border-2 student-card"
                      style={{
                        cursor: "pointer",
                        transition: "all 0.3s ease",
                        border: "2px solid #e0e0e0",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = "#007bff";
                        e.currentTarget.style.transform = "translateY(-5px)";
                        e.currentTarget.style.boxShadow =
                          "0 5px 15px rgba(0,123,255,0.3)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = "#e0e0e0";
                        e.currentTarget.style.transform = "translateY(0)";
                        e.currentTarget.style.boxShadow = "none";
                      }}
                      onClick={() => handleSelectStudent(student)}
                    >
                      <div className="card-body text-center">
                        <div className="mb-3">
                          <div
                            className="rounded-circle bg-primary bg-opacity-10 d-inline-flex align-items-center justify-content-center"
                            style={{ width: "60px", height: "60px" }}
                          >
                            <i className="bi bi-person-fill text-primary fs-3"></i>
                          </div>
                        </div>
                        <h5 className="card-title text-primary mb-3">
                          {student.studentName}
                        </h5>
                        <div className="mb-2">
                          <span className="badge bg-light text-dark me-2">
                            <i className="bi bi-book me-1"></i>
                            {student.className}
                          </span>
                          <span className="badge bg-light text-dark">
                            <i className="bi bi-diagram-3 me-1"></i>
                            {student.sectionName}
                          </span>
                        </div>
                        <p className="card-text small text-muted mb-3">
                          Student Code: {student.code}
                        </p>
                        <button
                          className="btn btn-primary w-100"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSelectStudent(student);
                          }}
                          disabled={loading}
                        >
                          {loading ? (
                            <span className="spinner-border spinner-border-sm me-2" />
                          ) : (
                            <i className="bi bi-arrow-right-circle me-2"></i>
                          )}
                          Select Student
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* PTM Booking Screen - Only show after student selection */}
      {selectedStudent && ptmEvent && (
        <div className="fade-in">
          {/* Header with Student Info */}
          <div className="card shadow-sm mb-4">
            <div className="card-body bg-light">
              <div className="row align-items-center">
                <div className="col-md-8">
                  <h3 className="mb-2 text-primary">
                    <i className="bi bi-calendar-event me-2"></i>
                    Parent-Teacher Meeting
                  </h3>
                  <div className="d-flex flex-wrap gap-3">
                    <span>
                      <i className="bi bi-person-fill text-muted me-1"></i>
                      <strong>Student:</strong> {studentName}
                    </span>
                    <span>
                      <i className="bi bi-calendar text-muted me-1"></i>
                      <strong>Event:</strong> {ptmEvent.title}
                    </span>
                    <span>
                      <i className="bi bi-clock text-muted me-1"></i>
                      <strong>Date:</strong>{" "}
                      {new Date(ptmEvent.eventDate).toLocaleDateString()}
                    </span>
                  </div>
                </div>
                <div className="col-md-4 text-end">
                  {students.length > 1 && (
                    <button
                      className="btn btn-outline-secondary"
                      onClick={handleChangeStudent}
                    >
                      <i className="bi bi-arrow-left-circle me-2"></i>
                      Change Student
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <ul className="nav nav-tabs nav-fill mb-4" role="tablist">
            <li className="nav-item">
              <button
                className={`nav-link ${
                  activeTab === "booking" ? "active" : ""
                }`}
                onClick={() => setActiveTab("booking")}
              >
                <i className="bi bi-calendar-plus me-2"></i>
                Available Slots
                <span className="badge bg-secondary ms-2">
                  {teachers.reduce(
                    (acc, t) =>
                      acc +
                      t.availableSlots.filter((s) => s.isAvailable).length,
                    0
                  )}
                </span>
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${
                  activeTab === "my-bookings" ? "active" : ""
                }`}
                onClick={() => setActiveTab("my-bookings")}
              >
                <i className="bi bi-calendar-check me-2"></i>
                My Bookings
                <span className="badge bg-success ms-2">
                  {myBookings.length}
                </span>
              </button>
            </li>
          </ul>

          {/* Available Slots Tab */}
          {activeTab === "booking" && (
            <div className="row g-4">
              {loading ? (
                <div className="col-12 text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : teachers.length === 0 ? (
                <div className="col-12">
                  <div className="alert alert-warning">
                    <i className="bi bi-info-circle me-2"></i>
                    No teachers available for booking at this time.
                  </div>
                </div>
              ) : (
                teachers.map((teacher) => (
                  <div
                    key={teacher.ptmEventTeacherId}
                    className="col-md-6 col-lg-4"
                  >
                    <div className="card h-100 shadow-sm">
                      <div className="card-header bg-white">
                        <h5 className="mb-1">
                          <i className="bi bi-person-workspace me-2 text-primary"></i>
                          {teacher.teacherName}
                        </h5>
                        <small className="text-muted">
                          <i className="bi bi-book me-1"></i>
                          {teacher.classSectionName}
                        </small>
                      </div>
                      <div className="card-body">
                        <div className="mb-3">
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <span className="text-muted small">
                              Available Slots
                            </span>
                            <span className="badge bg-primary">
                              {
                                teacher.availableSlots.filter(
                                  (s) => s.isAvailable
                                ).length
                              }
                            </span>
                          </div>
                          <div className="progress" style={{ height: "5px" }}>
                            <div
                              className="progress-bar bg-success"
                              style={{
                                width: `${
                                  (teacher.availableSlots.filter(
                                    (s) => s.isAvailable
                                  ).length /
                                    teacher.availableSlots.length) *
                                  100
                                }%`,
                              }}
                            />
                          </div>
                        </div>

                        {teacher.availableSlots.length === 0 ? (
                          <div className="alert alert-sm alert-warning mb-0">
                            No slots configured
                          </div>
                        ) : (
                          <div className="d-flex flex-wrap gap-2">
                            {teacher.availableSlots.map((slot) => {
                              const isAvailable = slot.isAvailable;
                              const isBookedByMe = slot.isBookedByMe;

                              return (
                                <button
                                  key={slot.slotId}
                                  className={`btn btn-sm ${
                                    isBookedByMe
                                      ? "btn-warning"
                                      : isAvailable
                                      ? "btn-outline-success"
                                      : "btn-outline-secondary"
                                  }`}
                                  disabled={!isAvailable || loading}
                                  onClick={() =>
                                    handleBookSlot(
                                      teacher.ptmEventTeacherId,
                                      slot.slotId
                                    )
                                  }
                                  title={
                                    isBookedByMe
                                      ? "Already booked by you"
                                      : isAvailable
                                      ? "Click to book this slot"
                                      : "Slot not available"
                                  }
                                  style={{ minWidth: "80px" }}
                                >
                                  {isBookedByMe && (
                                    <i className="bi bi-check-circle-fill me-1"></i>
                                  )}
                                  {slot.slotTimeFormatted}
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {/* My Bookings Tab */}
          {activeTab === "my-bookings" && (
            <div className="row g-4">
              {myBookings.length === 0 ? (
                <div className="col-12">
                  <div className="alert alert-info">
                    <i className="bi bi-info-circle me-2"></i>
                    You have not booked any slots yet. Go to the "Available
                    Slots" tab to book a meeting.
                  </div>
                </div>
              ) : (
                myBookings.map((booking) => (
                  <div key={booking.id} className="col-md-6 col-lg-4">
                    <div className="card h-100 border-success shadow-sm">
                      <div className="card-header bg-success text-white">
                        <h6 className="mb-0">
                          <i className="bi bi-check-circle-fill me-2"></i>
                          Booking Confirmed
                        </h6>
                      </div>
                      <div className="card-body">
                        <dl className="row mb-0">
                          <dt className="col-5">Student:</dt>
                          <dd className="col-7">{booking.studentName}</dd>

                          <dt className="col-5">Teacher:</dt>
                          <dd className="col-7 fw-bold">
                            {booking.teacherName}
                          </dd>

                          <dt className="col-5">Section:</dt>
                          <dd className="col-7">{booking.classSectionName}</dd>

                          <dt className="col-5">Date:</dt>
                          <dd className="col-7">
                            {new Date(booking.eventDate).toLocaleDateString()}
                          </dd>

                          <dt className="col-5">Time:</dt>
                          <dd className="col-7">
                            <span className="badge bg-primary">
                              {booking.slotTimeFormatted}
                            </span>
                          </dd>
                        </dl>
                      </div>
                      <div className="card-footer bg-white">
                        <button
                          className="btn btn-outline-danger w-100"
                          onClick={() => handleCancelBooking(booking.id)}
                          disabled={loading}
                        >
                          {loading ? (
                            <span className="spinner-border spinner-border-sm me-2" />
                          ) : (
                            <i className="bi bi-x-circle me-2"></i>
                          )}
                          Cancel Booking
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default PtmBookingParent;
